
import React, { useState, useMemo, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronDown, Plus, CheckCircle2, X, Save, LayoutGrid, Rows } from 'lucide-react';
import { supabase } from '../supabaseClient';

interface Task {
    id: number | string;
    title: string;
    date: string; // Full date in YYYY-MM-DD format
    dayOfWeek?: string; // Day of week (Monday, Tuesday, etc.)
    type: string;
    completed: boolean;
    notes?: string;
}

// Sub-component for Daily Tasks Modal
const DailyTasksModal: React.FC<{
    day: number;
    tasks: Task[];
    onClose: () => void;
    onToggleComplete: (id: string | number) => void;
}> = ({ day, tasks, onClose, onToggleComplete }) => {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fade-in">
             <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-white/60 overflow-hidden flex flex-col max-h-[85vh]">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
                    <h3 className="text-lg font-semibold text-slate-900">Tasks for December {day}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-0 custom-scrollbar">
                    {tasks.length === 0 ? (
                        <div className="p-12 text-center flex flex-col items-center justify-center text-slate-400">
                            <CheckCircle2 size={48} className="mb-4 text-slate-200" strokeWidth={1} />
                            <p className="text-sm">No tasks scheduled for this day.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 w-1/3">Task</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 w-1/2">Notes</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 text-center">Done?</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {tasks.map(task => (
                                    <tr key={task.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-6 py-4 text-sm font-medium text-slate-800 align-top">
                                            <div className="flex items-start space-x-2">
                                                <span className={`mt-1.5 w-2 h-2 flex-shrink-0 rounded-full ${
                                                    task.type === 'Urgent' ? 'bg-red-500' : 
                                                    task.type === 'Meeting' ? 'bg-purple-500' : 
                                                    task.type === 'Client' ? 'bg-blue-500' :
                                                    task.type === 'Dev' ? 'bg-indigo-500' :
                                                    'bg-slate-500'
                                                }`}></span>
                                                <div className="flex flex-col">
                                                    <span className={`transition-all ${task.completed ? 'line-through text-slate-400' : ''}`}>
                                                        {task.title}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{task.type}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600 align-top whitespace-pre-wrap">
                                            {task.notes ? (
                                                <p className={task.completed ? 'text-slate-300' : ''}>{task.notes}</p>
                                            ) : (
                                                <span className="text-slate-300 italic text-xs">No additional notes</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center align-top pt-5">
                                            <div className="flex justify-center">
                                                <input 
                                                    type="checkbox" 
                                                    checked={task.completed} 
                                                    onChange={() => onToggleComplete(task.id)}
                                                    className="w-5 h-5 rounded border-slate-300 text-slate-600 focus:ring-slate-500 focus:ring-offset-0 transition-all cursor-pointer checked:bg-emerald-600 checked:border-emerald-600"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-slate-100 transition-colors font-medium shadow-sm">
                        Close
                    </button>
                </div>
             </div>
        </div>
    );
};

const ToDoListPage: React.FC = () => {
  // Helper function to get day name from date string
  const getDayName = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  // Tasks state - loaded from Supabase
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  // Load tasks from Supabase on component mount
  useEffect(() => {
    let isMounted = true;
    
    loadTasksFromSupabase();
    
    // Timeout fallback - if Supabase doesn't respond in 5 seconds, show empty state
    const timeout = setTimeout(() => {
      if (isMounted) {
        setIsLoadingTasks(false);
      }
    }, 5000);
    
    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, []);

  const loadTasksFromSupabase = async () => {
    try {
      setIsLoadingTasks(true);
      
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('date', { ascending: true });

      if (error) {
        console.error('[ToDoListPage] Error loading tasks:', error);
        // Don't return - set loading to false so UI can render
        setIsLoadingTasks(false);
        return;
      }

      // Map category back to type (UI uses capitalized, DB uses lowercase)
      const categoryToTypeMap: Record<string, string> = {
        'urgent': 'Urgent',
        'meeting': 'Meeting',
        'dev': 'Dev',
        'client': 'Client',
        'admin': 'Admin',
        'general': 'General'
      };

      // Transform Supabase data to Task format
      const transformedTasks: Task[] = (data || []).map((task: any) => ({
        id: task.id,
        title: task.title,
        date: normalizeDate(task.date), // Normalize to ensure YYYY-MM-DD format
        dayOfWeek: getDayName(task.date),
        type: categoryToTypeMap[task.category] || task.category, // Map category to type
        completed: task.is_done,
        notes: task.notes || ''
      }));

      setTasks(transformedTasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      // Always set loading to false, even on error
    } finally {
      setIsLoadingTasks(false);
    }
  };

  // Add Task Modal State
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [addTaskFormData, setAddTaskFormData] = useState({
      title: '',
      dayOfWeek: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      type: 'General'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Week Filter State
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);

  // Modal State for "Daily View"
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isDailyViewOpen, setIsDailyViewOpen] = useState(false);
  
  // View Mode State: 'month' | 'week'
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);

  // Current Date State (for month/year navigation)
  const [currentDate, setCurrentDate] = useState(new Date(2025, 11, 2)); // December 2, 2025
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-indexed
  const currentDay = currentDate.getDate();
  
  // Calculate days in current month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Helper function to normalize dates to YYYY-MM-DD format (timezone-safe)
  const normalizeDate = (date: Date | string): string => {
    if (typeof date === 'string') {
      // If it's already a string, ensure it's in YYYY-MM-DD format
      // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
      const dateOnly = date.split('T')[0];
      // Validate it's in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        return dateOnly;
      }
      // If not in correct format, try to parse it
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      // Fallback: return as-is if we can't parse it
      return dateOnly;
    }
    // Convert Date object to YYYY-MM-DD without timezone issues
    // Use local date methods to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get tasks for a specific date
  const getTasksForDate = (dateString: string) => {
    const normalizedDate = normalizeDate(dateString);
    let filteredTasks = tasks.filter(t => normalizeDate(t.date) === normalizedDate);
    
    // Apply week filter if selected
    if (selectedWeek) {
      const [weekStart, weekEnd] = selectedWeek.split('|');
      filteredTasks = filteredTasks.filter(t => {
        const taskDate = normalizeDate(t.date);
        return taskDate >= weekStart && taskDate <= weekEnd;
      });
    }
    
    return filteredTasks;
  };

  // Get tasks for a day number (for calendar display)
  const getTasksForDay = (day: number) => {
    const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return getTasksForDate(dateString);
  };

  // Generate all weeks of the year
  const generateAllWeeks = () => {
    const weeks: Array<{ weekNo: number; startDate: string; endDate: string; label: string }> = [];
    
    for (let weekNo = 0; weekNo <= 52; weekNo++) {
      // Get the first Monday of the year
      const jan1 = new Date(currentYear, 0, 1);
      const jan1Day = jan1.getDay();
      const daysToMonday = jan1Day === 0 ? 1 : (8 - jan1Day) % 7 || 7;
      const firstMonday = new Date(currentYear, 0, 1 + daysToMonday);
      
      // Calculate start date (Monday) for this week
      const weekStart = new Date(firstMonday);
      weekStart.setDate(firstMonday.getDate() + (weekNo * 7));
      
      // Calculate end date (Sunday) for this week
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      // Format dates as DD.MM
      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}`;
      };
      
      const startDateStr = weekStart.toISOString().split('T')[0];
      const endDateStr = weekEnd.toISOString().split('T')[0];
      
      weeks.push({
        weekNo,
        startDate: startDateStr,
        endDate: endDateStr,
        label: `Week ${weekNo}: ${formatDate(weekStart)} - ${formatDate(weekEnd)}`
      });
    }
    
    return weeks;
  };

  const allWeeks = generateAllWeeks();

  // Get current week details for display (reactive - recalculates when currentDate changes)
  const weekDetails = useMemo(() => {
    const today = new Date(currentYear, currentMonth, currentDay);
    const jan1 = new Date(currentYear, 0, 1);
    const jan1Day = jan1.getDay();
    const daysToMonday = jan1Day === 0 ? 1 : (8 - jan1Day) % 7 || 7;
    const firstMonday = new Date(currentYear, 0, 1 + daysToMonday);
    
    const diffTime = today.getTime() - firstMonday.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNo = Math.floor(diffDays / 7);
    
    const weekStart = new Date(firstMonday);
    weekStart.setDate(firstMonday.getDate() + (weekNo * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const weekDays: number[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        weekDays.push(date.getDate());
      }
    }
    
    return { weekNo, weekStart, weekEnd, weekDays };
  }, [currentYear, currentMonth, currentDay]);

  // Determine which days to render based on viewMode
  const daysToRender = viewMode === 'month' 
      ? Array.from({ length: daysInMonth }, (_, i) => i + 1)
      : weekDetails.weekDays;

  // Header Label
  const getHeaderLabel = () => {
      if (viewMode === 'month') {
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                             'July', 'August', 'September', 'October', 'November', 'December'];
          return `${monthNames[currentMonth]} ${currentYear}`;
      } else {
          // Format dates (e.g., 01.12 - 07.12)
          const formatD = (d: number) => d.toString().padStart(2, '0');
          const formatMonth = (d: Date) => String(d.getMonth() + 1).padStart(2, '0');
          return `Week ${weekDetails.weekNo}: ${formatD(weekDetails.weekStart.getDate())}.${formatMonth(weekDetails.weekStart)} - ${formatD(weekDetails.weekEnd.getDate())}.${formatMonth(weekDetails.weekEnd)}`;
      }
  };

  // Handle month/year change
  const handleMonthYearChange = (year: number, month: number) => {
      setCurrentDate(new Date(year, month, 1));
      setIsMonthPickerOpen(false);
      // If in week mode, switch to month mode when changing month
      if (viewMode === 'week') {
          setViewMode('month');
      }
  };

  // Handlers
  const handleViewSelection = (mode: 'month' | 'week') => {
      setViewMode(mode);
      setIsViewMenuOpen(false);
  };

  const handleAddTask = async () => {
      if (!addTaskFormData.title || !addTaskFormData.date) return;
      
      setIsSaving(true);
      try {
          // Normalize the date to ensure consistent format
          const normalizedDate = normalizeDate(addTaskFormData.date);
          
          // Map type to category (Supabase uses 'category' field)
          const categoryMap: Record<string, string> = {
            'Urgent': 'urgent',
            'Meeting': 'meeting',
            'Dev': 'dev',
            'Client': 'client',
            'Admin': 'admin',
            'General': 'general'
          };
          const category = categoryMap[addTaskFormData.type] || 'general';

          // Insert into Supabase
          const { data, error } = await supabase
            .from('tasks')
            .insert([
              {
                title: addTaskFormData.title,
                date: normalizedDate,
                category: category,
                notes: addTaskFormData.notes || null,
                is_done: false
              }
            ])
            .select()
            .single();

          if (error) {
            console.error('Error saving task to Supabase:', error);
            throw error;
          }

          // Transform and add to local state
          const dayOfWeek = addTaskFormData.dayOfWeek || getDayName(normalizedDate);
          const newTask: Task = {
              id: data.id,
              title: data.title,
              date: normalizeDate(data.date), // Normalize to ensure consistent format
              dayOfWeek: dayOfWeek,
              type: addTaskFormData.type,
              completed: data.is_done,
              notes: data.notes || ''
          };

          setTasks(prev => [...prev, newTask]);
          
          // Reset form and close modal
          setAddTaskFormData({
              title: '',
              dayOfWeek: '',
              date: new Date().toISOString().split('T')[0],
              notes: '',
              type: 'General'
          });
          setIsAddTaskModalOpen(false);
      } catch (error) {
          console.error("Failed to save task", error);
          alert('Failed to save task. Please check console for details.');
      } finally {
          setIsSaving(false);
      }
  };

  const handleCloseAddTaskModal = () => {
      setIsAddTaskModalOpen(false);
      setAddTaskFormData({
          title: '',
          dayOfWeek: '',
          date: new Date().toISOString().split('T')[0],
          notes: '',
          type: 'General'
      });
  };

  const handleWeekFilterChange = (weekValue: string) => {
      if (weekValue === '') {
          setSelectedWeek(null);
      } else {
          setSelectedWeek(weekValue);
      }
  };

  const handleDayClick = (day: number) => {
      setSelectedDay(day);
      setIsDailyViewOpen(true);
  };

  // Filter tasks by selected week for the Weekly List (reactive - uses useMemo to recalculate when tasks or selectedWeek changes)
  const weeklyTasks = useMemo(() => {
      let weekStart: string;
      let weekEnd: string;
      
      if (!selectedWeek) {
          // If no week selected, show current week
          // Use normalizeDate to ensure consistent format
          weekStart = normalizeDate(weekDetails.weekStart);
          weekEnd = normalizeDate(weekDetails.weekEnd);
      } else {
          [weekStart, weekEnd] = selectedWeek.split('|');
          // Ensure they're normalized
          weekStart = normalizeDate(weekStart);
          weekEnd = normalizeDate(weekEnd);
      }
      
      // Filter tasks with normalized date comparison (inclusive boundaries)
      // Ensure all dates are normalized to YYYY-MM-DD strings for consistent comparison
      const filtered = tasks.filter(t => {
          // Normalize task date to YYYY-MM-DD string (no time, no timezone)
          const taskDate = normalizeDate(t.date);
          // Compare as strings (YYYY-MM-DD format allows string comparison)
          return taskDate >= weekStart && taskDate <= weekEnd;
      });
      
      // Sort by date, then by completion status
      return filtered.sort((a, b) => {
          const dateA = normalizeDate(a.date);
          const dateB = normalizeDate(b.date);
          if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
          }
          return a.completed === b.completed ? 0 : a.completed ? 1 : -1;
      });
  }, [tasks, selectedWeek, weekDetails]);

  // Format date for display (DD.MM)
  const formatDateDisplay = (dateString: string) => {
      const date = new Date(dateString + 'T00:00:00');
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}`;
  };

  const handleTaskComplete = async (taskId: string | number) => {
      try {
          // Find the task to get current state
          const task = tasks.find(t => t.id === taskId);
          if (!task) return;

          const newCompletedState = !task.completed;

          // Update in Supabase
          const { error } = await supabase
            .from('tasks')
            .update({ is_done: newCompletedState })
            .eq('id', taskId);

          if (error) {
            console.error('Error updating task in Supabase:', error);
            throw error;
          }

          // Update local state
          setTasks(prevTasks => prevTasks.map(t => {
              if (t.id === taskId) {
                  return { ...t, completed: newCompletedState };
              }
              return t;
          }));
      } catch (error) {
          console.error("Failed to update task status", error);
      }
  };

  // Show loading state while tasks are being loaded
  if (isLoadingTasks) {
    return (
      <div className="animate-fade-in flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full animate-spin mb-4"></div>
        <p className="text-sm text-slate-500">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in flex flex-col space-y-6 relative w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">To Do List</h1>
          <p className="text-slate-500">Manage your tasks and schedule</p>
        </div>
        
        {/* Actions Toolbar */}
        <div className="flex items-center space-x-3 w-full md:w-auto flex-wrap gap-y-2">
             {/* Week Filter Dropdown */}
             <div className="relative">
                <select
                    value={selectedWeek || ''}
                    onChange={(e) => handleWeekFilterChange(e.target.value)}
                    className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm min-w-[220px] appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                >
                    <option value="">All Weeks</option>
                    {allWeeks.map(week => (
                        <option key={week.weekNo} value={`${week.startDate}|${week.endDate}`}>
                            {week.label}
                        </option>
                    ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
             </div>
             
             {/* View Mode Dropdown */}
             <div className="relative">
                <button 
                    onClick={() => setIsViewMenuOpen(!isViewMenuOpen)}
                    className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm min-w-[150px] justify-between"
                >
                    <div className="flex items-center space-x-2">
                        {viewMode === 'month' ? <LayoutGrid size={16} /> : <Rows size={16} />}
                        <span>{viewMode === 'month' ? 'Month View' : 'Week View'}</span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isViewMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isViewMenuOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsViewMenuOpen(false)}></div>
                        <div className="absolute top-full left-0 md:left-auto md:right-0 mt-2 w-full min-w-[160px] bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-fade-in overflow-hidden">
                            <button 
                                onClick={() => handleViewSelection('month')}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center space-x-2 hover:bg-slate-50 transition-colors ${viewMode === 'month' ? 'text-slate-900 bg-slate-100 font-medium' : 'text-slate-600'}`}
                            >
                                <LayoutGrid size={16} className={viewMode === 'month' ? 'text-slate-900' : 'text-slate-400'} />
                                <span>Month View</span>
                                {viewMode === 'month' && <CheckCircle2 size={14} className="ml-auto text-slate-900" />}
                            </button>
                            <button 
                                onClick={() => handleViewSelection('week')}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center space-x-2 hover:bg-slate-50 transition-colors ${viewMode === 'week' ? 'text-slate-900 bg-slate-100 font-medium' : 'text-slate-600'}`}
                            >
                                <Rows size={16} className={viewMode === 'week' ? 'text-slate-900' : 'text-slate-400'} />
                                <span>Week View</span>
                                {viewMode === 'week' && <CheckCircle2 size={14} className="ml-auto text-slate-900" />}
                            </button>
                        </div>
                    </>
                )}
             </div>

             {/* Month/Year Picker */}
             <div className="relative">
                <button 
                    onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}
                    className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm min-w-[160px] justify-center whitespace-nowrap"
                >
                <CalendarIcon size={16} />
                <span className="capitalize">{getHeaderLabel()}</span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isMonthPickerOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMonthPickerOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsMonthPickerOpen(false)}></div>
                        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 p-4 z-50 animate-fade-in">
                            <div className="space-y-4">
                                {/* Year Selector */}
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-2">Year</label>
                                    <select
                                        value={currentYear}
                                        onChange={(e) => handleMonthYearChange(parseInt(e.target.value), currentMonth)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white text-slate-700"
                                    >
                                        {Array.from({ length: 10 }, (_, i) => currentYear - 2 + i).map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Month Selector */}
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-2">Month</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleMonthYearChange(currentYear, index)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                    currentMonth === index
                                                        ? 'bg-slate-900 text-white'
                                                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                                                }`}
                                            >
                                                {month}
            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
             </div>
            
            {/* Add Task Button */}
            <button
                onClick={() => {
                  // Reset form with today's date when opening modal
                  setAddTaskFormData({
                    title: '',
                    dayOfWeek: '',
                    date: new Date().toISOString().split('T')[0],
                    notes: '',
                    type: 'General'
                  });
                  setIsAddTaskModalOpen(true);
                }}
                className="flex items-center space-x-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium shadow-lg shadow-slate-200 active:scale-95 duration-150 whitespace-nowrap"
            >
                <Plus size={16} />
                <span className="hidden sm:inline">Add Task</span>
                <span className="sm:hidden">Add</span>
            </button>
        </div>
      </div>

      {/* Weekly Task List */}
      <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white/50">
          <h2 className="text-lg font-medium text-slate-900">
            Weekly Tasks
            {selectedWeek && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({allWeeks.find(w => `${w.startDate}|${w.endDate}` === selectedWeek)?.label || 'Selected Week'})
              </span>
            )}
          </h2>
        </div>
        
        {weeklyTasks.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <p className="text-sm">No tasks for this week. Click "Add Task" to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    <span className="sr-only">Status</span>
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Task Name</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Day</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes Preview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {weeklyTasks.map(task => (
                  <tr 
                    key={task.id} 
                    className={`hover:bg-slate-50/50 transition-colors ${task.completed ? 'opacity-60' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={() => handleTaskComplete(task.id)}
                        className="w-5 h-5 rounded border-slate-300 text-slate-600 focus:ring-slate-500 focus:ring-offset-0 transition-all cursor-pointer checked:bg-emerald-600 checked:border-emerald-600"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <span className={`w-2 h-2 rounded-full ${
                          task.type === 'Urgent' ? 'bg-red-500' : 
                          task.type === 'Meeting' ? 'bg-purple-500' : 
                          task.type === 'Client' ? 'bg-blue-500' :
                          task.type === 'Dev' ? 'bg-indigo-500' :
                          'bg-slate-400'
                        }`}></span>
                        <span className={`text-sm font-medium text-slate-900 ${task.completed ? 'line-through text-slate-400' : ''}`}>
                          {task.title}
                        </span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider px-2 py-0.5 bg-slate-100 rounded">
                          {task.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {task.dayOfWeek || getDayName(task.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDateDisplay(task.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500 max-w-xs truncate">
                      {task.notes || <span className="text-slate-300 italic">No notes</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Calendar Grid - Full Size */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[700px] flex-shrink-0 transition-all duration-300">
        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-slate-200">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50">
                {day}
              </div>
            ))}
        </div>

        {/* Calendar Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar min-h-[600px]">
            <div className={`
                grid grid-cols-7 bg-slate-100 gap-px
                ${viewMode === 'month' ? 'auto-rows-fr' : 'auto-rows-auto'}
            `}>
                {daysToRender.map(day => {
                    const dayTasks = getTasksForDay(day);
                    const today = new Date();
                    const isToday = day === today.getDate() && 
                                   currentMonth === today.getMonth() && 
                                   currentYear === today.getFullYear();

                    return (
                        <div 
                            key={day} 
                            onClick={() => handleDayClick(day)}
                            className={`
                                bg-white p-3 relative group hover:bg-slate-50/50 transition-colors cursor-pointer flex flex-col
                                ${viewMode === 'month' ? 'min-h-[120px]' : 'min-h-[150px]'}
                                ${isToday ? 'bg-slate-50/10' : ''}
                            `}
                        >
                            {/* Date Number */}
                            <div className="flex justify-between items-start mb-2 flex-shrink-0">
                                <span className={`
                                    text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                                    ${isToday ? 'bg-slate-900 text-white shadow-md shadow-slate-200' : 'text-slate-400'}
                                `}>
                                    {day}
                                </span>
                                <button className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-slate-900 transition-all">
                                    <Plus size={14} />
                                </button>
                            </div>

                            {/* Tasks */}
                            <div className="space-y-1.5 flex-1 overflow-y-auto custom-scrollbar pr-1">
                                {dayTasks.map(task => (
                                    <div 
                                        key={task.id} 
                                        className={`
                                            px-2 py-1.5 rounded-md border text-xs font-medium truncate transition-all hover:scale-[1.02]
                                            ${task.completed ? 'bg-slate-100 text-slate-400 border-slate-200 line-through' : 
                                              task.type === 'Urgent' ? 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100 hover:border-red-200' :
                                              task.type === 'Meeting' ? 'bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-100 hover:border-purple-200' :
                                              'bg-white border-slate-200 text-slate-700 shadow-sm hover:border-slate-300'}
                                        `}
                                    >
                                        <div className="flex items-center space-x-1.5">
                                            {task.completed ? <CheckCircle2 size={10} className="text-emerald-600" /> : <div className={`w-1.5 h-1.5 rounded-full ${task.type === 'Urgent' ? 'bg-red-500' : task.type === 'Meeting' ? 'bg-purple-500' : 'bg-slate-500'}`}></div>}
                                            <span className="truncate">{task.title}</span>
                                        </div>
                                        {/* Show extra details in Week view if space permits, currently simplified for consistency */}
                                        {viewMode === 'week' && task.notes && (
                                            <p className="text-[10px] text-slate-400 mt-1 truncate pl-3 hidden xl:block">
                                                {task.notes}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
      </div>

      {/* Add Task Modal */}
      {isAddTaskModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fade-in">
             <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-white/60 overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Add New Task</h3>
              <button onClick={handleCloseAddTaskModal} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={20} />
              </button>
                </div>
                
                <div className="p-6 space-y-4">
              {/* Row 1: Task Description */}
                    <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Task *</label>
                        <input 
                            type="text" 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white"
                  placeholder="Enter task description..."
                  value={addTaskFormData.title}
                  onChange={(e) => setAddTaskFormData({...addTaskFormData, title: e.target.value})}
                            autoFocus
                        />
                    </div>

              {/* Row 2: Day of Week */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Day</label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white text-slate-700"
                  value={addTaskFormData.dayOfWeek}
                  onChange={(e) => setAddTaskFormData({...addTaskFormData, dayOfWeek: e.target.value})}
                >
                  <option value="">Select day of week</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>

              {/* Row 3: Date */}
                        <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Date *</label>
                            <input 
                                type="date" 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white text-slate-700"
                  value={addTaskFormData.date}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setAddTaskFormData({
                      ...addTaskFormData,
                      date: newDate,
                      dayOfWeek: addTaskFormData.dayOfWeek || getDayName(newDate)
                    });
                  }}
                />
              </div>

              {/* Row 4: Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white resize-none h-24"
                  placeholder="Add any additional notes..."
                  value={addTaskFormData.notes}
                  onChange={(e) => setAddTaskFormData({...addTaskFormData, notes: e.target.value})}
                            />
                        </div>

              {/* Category/Type */}
                        <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
                            <select 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none bg-white text-slate-700"
                  value={addTaskFormData.type}
                  onChange={(e) => setAddTaskFormData({...addTaskFormData, type: e.target.value})}
                            >
                                <option value="General">General</option>
                                <option value="Urgent">Urgent</option>
                                <option value="Meeting">Meeting</option>
                                <option value="Dev">Dev</option>
                                <option value="Client">Client</option>
                                <option value="Admin">Admin</option>
                            </select>
                    </div>
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end space-x-3">
                    <button 
                        onClick={handleCloseAddTaskModal}
                        className="px-4 py-2 text-slate-600 font-medium text-sm hover:bg-slate-100 rounded-lg transition-colors"
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button 
                onClick={handleAddTask}
                disabled={!addTaskFormData.title || !addTaskFormData.date || isSaving}
                className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        {isSaving ? (
                            <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                            </>
                        ) : (
                            <>
                    <Plus size={16} />
                    <span>Add Task</span>
                            </>
                        )}
                    </button>
                </div>
             </div>
        </div>
      )}

      {/* Daily View Modal */}
      {isDailyViewOpen && selectedDay !== null && (
          <DailyTasksModal 
            day={selectedDay}
            tasks={getTasksForDay(selectedDay)}
            onClose={() => setIsDailyViewOpen(false)}
            onToggleComplete={handleTaskComplete}
          />
      )}
    </div>
  );
};

export default ToDoListPage;
